import React from "react";
import { Button, Col, Empty, Row, Spin, Table } from "antd";
import { CopyOutlined, EditOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import {
  ANNOUNCEMENT_BAR_POSITION_LABELS,
  BANNER_SCREEN_LABELS,
  BANNER_SOURCE_LABELS,
  BANNER_TYPE,
  BANNER_TYPE_LABELS,
  POPUP_FREQUENCY_PERIOD_LABELS,
  POPUP_TRIGGER_DELAY_UNIT_LABELS,
  POPUP_TRIGGER_TYPE_LABELS,
  SELLER_SEGMENT_LABELS,
} from "../../../core/constants";
import { formatDateTimeRangeDisplay, formatNavigationSummary, getScreenDisplayNames } from "../../../core/utils";
import { useBannerDetail } from "../../../data/hooks";
import AnnouncementBarPreview from "../../components/AnnouncementBarPreview";
import BannerTimeStatusDot from "../../components/BannerTimeStatusDot";
import BannerTypeTag from "../../components/BannerTypeTag";
import "../../styles/marketingBanner.scss";

function BannerDetailPage() {
  const history = useHistory();
  const { id } = useParams();
  const { banner, loading } = useBannerDetail(id);

  if (loading) {
    return (
      <div className="marketing-banner-container" style={{ textAlign: "center", paddingTop: "100px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!banner) {
    return (
      <div className="marketing-banner-container">
        <Empty description="Không tìm thấy thông tin công cụ truyền thông." />
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <Button onClick={() => history.push("/marketing-banner")}>Quay lại danh sách</Button>
        </div>
      </div>
    );
  }

  const typeLabel = BANNER_TYPE_LABELS[banner.type] || banner.type;
  const timeDisplay = formatDateTimeRangeDisplay(banner.startTime, banner.endTime);
  const segmentDisplay = SELLER_SEGMENT_LABELS[banner.sellerSegmentType] || banner.sellerSegmentType || "—";
  const screensDisplay = getScreenDisplayNames(banner.screens || [], BANNER_SCREEN_LABELS);
  const navDisplay = formatNavigationSummary(banner.navigation);

  const handleCopyLink = () => {
    const textToCopy = banner.navigation?.url || banner.navigation?.targetName || banner.name;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      notify.success({ message: "Đã sao chép link điều hướng" });
    }
  };

  const productColumns = [
    {
      title: "STT",
      key: "idx",
      width: 60,
      align: "center",
      render: (_, __, i) => i + 1,
    },
    {
      title: "THÔNG TIN SẢN PHẨM",
      key: "name",
      render: (_, record) => (
        <div>
          <div style={{ color: "#2246dc", fontWeight: 500 }}>{record.code}</div>
          <div style={{ fontSize: "13px" }}>{record.name}</div>
        </div>
      ),
    },
    {
      title: "THƯƠNG HIỆU",
      dataIndex: "brand",
      key: "brand",
      width: 140,
      render: (brand, r) => brand?.name || r.brandName || "—",
    },
    {
      title: "NGÀNH HÀNG",
      dataIndex: "category",
      key: "category",
      width: 140,
      render: (cat, r) => cat?.name || r.categoryName || "—",
    },
    {
      title: "TRẠNG THÁI",
      key: "status",
      width: 150,
      render: (_, record) => {
        const isActive = record.isActive !== false;
        return (
          <span
            style={{
              padding: "2px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              color: isActive ? "#27a745" : "#e02020",
              border: `1px solid ${isActive ? "#c3e6cb" : "#f5c6cb"}`,
              backgroundColor: isActive ? "#f4fcf6" : "#fff5f5",
            }}
          >
            {isActive ? "Đang kinh doanh" : "Ngừng kinh doanh"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="marketing-banner-container">
      {/* Header */}
      <div className="banner-page-header">
        <div className="banner-breadcrumb">
          <span style={{ cursor: "pointer", color: "#2246dc" }} onClick={() => history.push("/marketing-banner")}>
            Quản lý công cụ truyền thông
          </span>{" "}
          / Xem chi tiết {typeLabel.toLowerCase()}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 className="banner-page-title">Xem chi tiết {typeLabel.toLowerCase()}</h1>
          <BannerTypeTag type={banner.type} />
        </div>
      </div>

      {/* Top Overview Card */}
      <div className="banner-detail-overview">
        <div className="overview-title">{banner.name}</div>
        <div className="overview-meta">
          <div>
            <BannerTimeStatusDot status={banner.timeStatus} />
          </div>
          <div>
            Thời gian diễn ra: <strong>{timeDisplay}</strong>
          </div>
          <div>
            Kênh hiển thị: <strong>App đại lý</strong>
          </div>
          <div>
            Khách hàng mục tiêu: <strong>{segmentDisplay}</strong>
          </div>
        </div>
      </div>

      {/* Main Info Card */}
      <div className="banner-section-card">
        <div className="banner-section-header">Thông tin {typeLabel.toLowerCase()}</div>
        <div className="banner-section-body">
          <Row gutter={24}>
            <Col span={14}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <tbody>
                  <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px 0", color: "#595959", width: "200px" }}>Nguồn</td>
                    <td style={{ padding: "12px 0", fontWeight: 500 }}>
                      {BANNER_SOURCE_LABELS[banner.source] || banner.source}
                    </td>
                  </tr>

                  <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px 0", color: "#595959" }}>Link điều hướng</td>
                    <td style={{ padding: "12px 0", fontWeight: 500, color: "#2246dc" }}>
                      <span>{navDisplay}</span>
                      {banner.navigation?.url && (
                        <CopyOutlined
                          onClick={handleCopyLink}
                          style={{ marginLeft: "8px", cursor: "pointer", color: "#595959" }}
                          title="Sao chép"
                        />
                      )}
                    </td>
                  </tr>

                  <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px 0", color: "#595959" }}>Màn hình hiển thị</td>
                    <td style={{ padding: "12px 0", fontWeight: 500 }}>{screensDisplay}</td>
                  </tr>

                  {/* Popup Details */}
                  {banner.type === BANNER_TYPE.POPUP && banner.popup && (
                    <>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Tổng số lần hiển thị / đại lý</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>{banner.popup.maxImpressions} lần</td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Thời lượng hiển thị</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {banner.popup.displayDurationSeconds != null
                            ? `${banner.popup.displayDurationSeconds} giây`
                            : "Không giới hạn"}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Thời điểm kích hoạt</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {POPUP_TRIGGER_TYPE_LABELS[banner.popup.triggerType] || banner.popup.triggerType}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Thời gian kích hoạt</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {banner.popup.triggerDelayValue}{" "}
                          {POPUP_TRIGGER_DELAY_UNIT_LABELS[banner.popup.triggerDelayUnit] || "Giây"}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Tần suất hiển thị</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {banner.popup.frequencyCount} lần trong {banner.popup.frequencyPeriodValue}{" "}
                          {POPUP_FREQUENCY_PERIOD_LABELS[banner.popup.frequencyPeriod] || "Ngày"}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Dừng hiển thị sau khi click</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>{banner.popup.stopAfterClicks} lần</td>
                      </tr>
                    </>
                  )}

                  {/* Announcement Bar Details */}
                  {banner.type === BANNER_TYPE.ANNOUNCEMENT_BAR && banner.announcementBar && (
                    <>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Vị trí</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {ANNOUNCEMENT_BAR_POSITION_LABELS[banner.announcementBar.position] ||
                            banner.announcementBar.position}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 0", color: "#595959" }}>Cho phép đại lý đóng</td>
                        <td style={{ padding: "12px 0", fontWeight: 500 }}>
                          {banner.announcementBar.allowClose ? "Có" : "Không"}
                        </td>
                      </tr>
                      {banner.announcementBar.message && (
                        <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "12px 0", color: "#595959" }}>Thông điệp</td>
                          <td style={{ padding: "12px 0", fontWeight: 500 }}>{banner.announcementBar.message}</td>
                        </tr>
                      )}
                      {banner.announcementBar.ctaText && (
                        <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "12px 0", color: "#595959" }}>Nút hành động (CTA)</td>
                          <td style={{ padding: "12px 0", fontWeight: 500 }}>{banner.announcementBar.ctaText}</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </Col>

            {/* Right column: Images */}
            <Col span={10}>
              {banner.type === BANNER_TYPE.BANNER && (
                <div>
                  {banner.imageURL && (
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ fontWeight: 500, fontSize: "13px", marginBottom: "4px" }}>
                        Banner (định dạng 1800x600)
                      </div>
                      <img
                        src={banner.imageURL}
                        alt="Banner ngang"
                        style={{
                          width: "100%",
                          maxHeight: "160px",
                          objectFit: "contain",
                          borderRadius: "6px",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </div>
                  )}

                  {banner.squareImageURL && (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "13px", marginBottom: "4px" }}>
                        Banner (định dạng 1500x1500)
                      </div>
                      <img
                        src={banner.squareImageURL}
                        alt="Banner vuông"
                        style={{
                          width: "160px",
                          height: "160px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {banner.type === BANNER_TYPE.POPUP && banner.imageURL && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: 500, fontSize: "13px", marginBottom: "8px" }}>
                    Pop-up Mobile (600 x 600 px)
                  </div>
                  <img
                    src={banner.imageURL}
                    alt="Popup"
                    style={{
                      maxWidth: "260px",
                      maxHeight: "260px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  />
                </div>
              )}

              {banner.type === BANNER_TYPE.ANNOUNCEMENT_BAR && banner.announcementBar && (
                <AnnouncementBarPreview
                  position={banner.announcementBar.position}
                  displayType={banner.announcementBar.displayType}
                  message={banner.announcementBar.message}
                  ctaText={banner.announcementBar.ctaText}
                  allowClose={banner.announcementBar.allowClose}
                  iconMode={banner.announcementBar.iconMode}
                  iconPresetCode={banner.announcementBar.iconPresetCode}
                  iconURL={banner.announcementBar.iconURL}
                  backgroundMode={banner.announcementBar.backgroundMode}
                  colorPresetCode={banner.announcementBar.colorPresetCode}
                  backgroundColor={banner.announcementBar.backgroundColor}
                  gradientFromColor={banner.announcementBar.gradientFromColor}
                  gradientToColor={banner.announcementBar.gradientToColor}
                  gradientAngle={banner.announcementBar.gradientAngle}
                  textColor={banner.announcementBar.textColor}
                  iconColor={banner.announcementBar.iconColor}
                  buttonColor={banner.announcementBar.buttonColor}
                  buttonTextColor={banner.announcementBar.buttonTextColor}
                  imageURL={banner.imageURL}
                />
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* Applicable Products (if any) */}
      {banner.applicableProducts && banner.applicableProducts.length > 0 && (
        <div className="banner-section-card">
          <div className="banner-section-header">Sản phẩm áp dụng ({banner.applicableProducts.length} sản phẩm)</div>
          <div className="banner-section-body">
            <Table
              dataSource={banner.applicableProducts}
              columns={productColumns}
              rowKey="id"
              pagination={banner.applicableProducts.length > 5 ? { pageSize: 5 } : false}
              size="small"
            />
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="banner-actions-footer">
        <Button className="btn-cancel" onClick={() => history.push("/marketing-banner")}>
          Hủy bỏ
        </Button>
        <Button
          type="primary"
          className="btn-submit"
          icon={<EditOutlined />}
          onClick={() => history.push(`/marketing-banner/edit/${banner.id}`)}
        >
          Chỉnh sửa
        </Button>
      </div>
    </div>
  );
}

export default BannerDetailPage;
